import React, { useState } from 'react';
import { string, func, arrayOf } from 'prop-types';
import classNames from 'classnames';
import css from './TranslateButton.module.css';

/**
 * TranslateButton — adds a "Translate" button to any text block.
 *
 * Usage (in ListingPage description section):
 *   <TranslateButton text={listing.attributes.description} />
 *
 * Or with a callback (if you want to update state):
 *   <TranslateButton
 *     text={description}
 *     onTranslated={(translatedText, lang) => setTranslatedDescription(translatedText)}
 *   />
 *
 * @param {string} props.text — original text to translate
 * @param {Function} [props.onTranslated] — callback with (translatedText, targetLang)
 * @param {string[]} [props.availableLanguages] — override supported languages list
 * @param {string} [props.className]
 */

const LANGUAGE_NAMES = {
  EN: 'English', DE: 'German', FR: 'French', ES: 'Spanish',
  IT: 'Italian', PT: 'Portuguese', NL: 'Dutch', PL: 'Polish',
  LV: 'Latvian', LT: 'Lithuanian', ET: 'Estonian', FI: 'Finnish', SV: 'Swedish',
};

const TranslateButton = ({ text, onTranslated, availableLanguages, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [translatedText, setTranslatedText] = useState(null);
  const [targetLang, setTargetLang] = useState(null);

  const langs = availableLanguages || Object.keys(LANGUAGE_NAMES);

  const handleTranslate = async lang => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, targetLang: lang }),
      });
      const data = await res.json();
      if (res.ok) {
        setTranslatedText(data.translatedText);
        setTargetLang(lang);
        setIsOpen(false);
        if (onTranslated) onTranslated(data.translatedText, lang);
      }
    } catch (err) {
      console.error('[TranslateButton] error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowOriginal = () => {
    setTranslatedText(null);
    setTargetLang(null);
    if (onTranslated) onTranslated(null, null);
  };

  return (
    <div className={classNames(css.root, className)}>
      {translatedText ? (
        <div className={css.translated}>
          <p className={css.translatedText}>{translatedText}</p>
          <button type="button" onClick={handleShowOriginal} className={css.showOriginal}>
            Show original
          </button>
        </div>
      ) : null}

      {!isOpen ? (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          disabled={isLoading}
          className={css.trigger}
        >
          🌐 {translatedText ? `Translated (${targetLang})` : 'Translate'}
        </button>
      ) : (
        <div className={css.langPicker}>
          <span className={css.label}>Translate to:</span>
          <div className={css.langs}>
            {langs.map(lang => (
              <button
                key={lang}
                type="button"
                onClick={() => handleTranslate(lang)}
                disabled={isLoading}
                className={classNames(css.langBtn, lang === targetLang && css.langBtnActive)}
              >
                {LANGUAGE_NAMES[lang] || lang}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setIsOpen(false)} className={css.cancel}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

TranslateButton.defaultProps = {
  onTranslated: null,
  availableLanguages: null,
  className: null,
};

TranslateButton.propTypes = {
  text: string.isRequired,
  onTranslated: func,
  availableLanguages: arrayOf(string),
  className: string,
};

export default TranslateButton;
